// @flow

import * as React from 'react';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  selectCartCurrency,
  removeProducts,
  useDiscountDetails,
  type ProductError as CartProductError,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import {
  selectMailboxesPrices,
  type MailboxPricesByType,
  mailboxes,
} from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';

import { Base } from '@gandi/ui';

import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { CountRow, TitleRow } from './components/Rows';
import { AutoRenewRow } from './components/Rows/AutoRenewRow';
import { DiscountRow } from './components/Rows/DiscountRow';
import TotalRow from './components/Rows/TotalRow';
import { useOnAutoRenewChange, useOnCounterChange } from './modules/mailbox';

export function MailboxStandAloneCreate({
  product,
}: {
  product: DecoratedProduct,
}): React.MixedElement {
  const dispatch = useDispatch();
  const { __, formatDate, formatPrice } = useTranslator();
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  const [loading, setLoading] = React.useState(false);

  const currency = useAppSelector(selectCartCurrency);

  const mailboxPriceParams = {
    process: 'create',
    productType: 'mailboxv2',
  };

  const prices: MailboxPricesByType = useAppSelector(state =>
    selectMailboxesPrices(state, mailboxPriceParams),
  );

  const discountDetails = useDiscountDetails({ product });

  const isFetchingPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsFetchingOne(
      state,
      'mailboxv2_create',
    ),
  );
  const isLoadedPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsLoadedOne(
      state,
      'mailboxv2_create',
    ),
  );

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const onAutoRenewChange = useOnAutoRenewChange(product);
  const onCounterChange = useOnCounterChange(product, setLoading, loading);

  React.useEffect(() => {
    if (!isFetchingPrices && !isLoadedPrices) {
      dispatch(fetchMailboxesPrices());
    }
  }, [dispatch, isFetchingPrices, isLoadedPrices]);

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const getPriceByMonth = () => {
    const {
      payload: { mailbox_type },
    } = product;

    if (!isEmpty(prices) && mailbox_type) {
      return get(prices, [mailbox_type, 'price']);
    }

    return undefined;
  };

  if (productErrors.length > 0) {
    return <ProductErrorItem label={undefined} errors={productErrors} />;
  }

  const mailboxType = product.payload.config_name;
  const priceByMonth = getPriceByMonth();
  const durationMonth = product.payload.duration_month;

  // $FlowFixMe[incompatible-call] - durationMonth is defined
  const expirationDate = moment().add(durationMonth, 'months');

  const detail =
    !!priceByMonth &&
    __('%(montlyPrice)s/ month/ mailbox', {
      montlyPrice: formatPrice(priceByMonth, currency),
    });

  const price = product.prices?.length
    ? product.computedTotalPrices?.beforeDiscount ||
      product.total?.before_discount
    : undefined;

  const capacity =
    mailboxType === 'standard' ? __('Mailbox 10 Go') : __('Mailbox 50 Go');

  return (
    <ProductItemBase>
      <TitleRow
        label={FF_CART_OVERVIEW_NDS ? __('Mailbox') : undefined}
        productType={product.productType}
        info={__('Expires on %(dateEnd)s', {
          dateEnd: formatDate(expirationDate, 'LL'),
        })}
        handleRemoveAction={removeFromCart}
      />

      <CountRow
        productId={product.id}
        /**
          We added || 1 just in case the api fails to return the quantity
         */
        count={product.payload?.quantity || 1}
        // $FlowFixMe[incompatible-call] config_name is always defined
        label={__('Mailbox %(name)s %(capacity)s %(plan)s', {
          capacity,
          name: product.payload.config_name,
          plan:
            product.payload.duration_month === 1
              ? __('monthly subscription')
              : __('yearly subscription'),
        })}
        price={loading ? undefined : price}
        showExcludeVatMention={product.hasTaxes}
        onChange={onCounterChange}
        isLoading={loading}
        detail={detail}
      />

      {/* TODO: Do we use the new AutoRenewBlock component */}
      <Base marginBottom="xsmall">
        <AutoRenewRow
          productId={product.id}
          checked={!!product.payload?.autorenew}
          onChange={onAutoRenewChange}
        />
      </Base>

      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          // $FlowFixMe[incompatible-type] - price can be a string
          price={discount.price}
        />
      ))}
      <TotalRow
        hasTaxes={false}
        totalPrice={
          product.computedTotalPrices?.beforeTaxes ??
          product.total?.before_taxes
        }
        hasDiscount={!!discountDetails?.length}
      />
    </ProductItemBase>
  );
}
